import React from 'react';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import L from 'leaflet'; // Import Leaflet library itself

// --- Leaflet CSS ---
import 'leaflet/dist/leaflet.css';

// --- Leaflet Icon Fix ---
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});
// --- End Icon Fix ---

// Check if the token is available (optional but good practice)
if (!ENV.MAPBOX_ACCESS_TOKEN) {
  console.error("Mapbox Access Token is missing. Make sure it's set on window.ENV.");
  // You might want to render an error message or a fallback here instead of null
}

interface SinglePinMapProps {
  latitude: number;
  longitude: number;
  pinType: string | null;
}

export function SinglePinMap({ latitude, longitude, pinType }: SinglePinMapProps) {
  const center: L.LatLngExpression = [latitude, longitude];
  const initialZoom = pinType === 'circle' ? 12 : 14;
  const minZoom = 0;
  const maxZoom = 18; // Mapbox max zoom can often go higher (e.g., 22), adjust if needed

  const mapStyle = { height: '300px', width: '100%' };

  // Choose a Mapbox Style ID (e.g., streets-v11, outdoors-v11, satellite-v9)
  // Find more standard styles here: https://docs.mapbox.com/api/maps/styles/
  const mapboxStyleId = 'mapbox/streets-v12'; // Or your preferred style

  // Construct the Mapbox Tile URL
  // Using @2x for retina tiles
  const mapboxTileUrl = `https://api.mapbox.com/styles/v1/${mapboxStyleId}/tiles/{z}/{x}/{y}@2x?access_token=${ENV.MAPBOX_ACCESS_TOKEN}`;

  // Mapbox Attribution (Required)
  const mapboxAttribution = `
    &copy; <a href="https://www.mapbox.com/about/maps/" target="_blank">Mapbox</a>
    &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors
    <a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a>`;

  return (
    <MapContainer
      center={center}
      zoom={initialZoom}
      minZoom={minZoom}
      maxZoom={maxZoom} // Adjust if needed for Mapbox style
      style={mapStyle}
      scrollWheelZoom={true}
    >
      {/* Only render TileLayer if the token is available */}
      {ENV.MAPBOX_ACCESS_TOKEN ? (
        <TileLayer
          attribution={mapboxAttribution}
          url={mapboxTileUrl}
          // These are important for retina (@2x) tiles from Mapbox
          tileSize={512}
          zoomOffset={-1}
        />
      ) : (
        // Optional: Render a message or placeholder if token is missing
         <div>Map cannot be displayed. Missing Mapbox configuration.</div>
      )}


      {/* Conditional Marker or Circle (only render if map tiles are potentially loading) */}
      {ENV.MAPBOX_ACCESS_TOKEN && (
        pinType === 'circle' ? (
          <Circle center={center} radius={1000} pathOptions={{ color: 'blue' }} />
        ) : (
          <Marker position={center} />
        )
      )}
    </MapContainer>
  );
}
